import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map, shareReplay } from 'rxjs/operators';

import { ApiUrl } from '../shared/models/apiurl.models';

/** SCRUM-259 — one business domain = an existing main category. */
export interface BusinessDomain {
  id: number;
  name: string;
  category: string;
  requires_license: boolean;
}

export interface BusinessConfig {
  tag_labels: Record<string, string>;
  private_filter_labels: Record<string, string>;
  business_filter_labels: Record<string, string>;
  regulated_domains: string[];
  grace_days: number;
  domains: BusinessDomain[];
}

/** SCRUM-265 — what the personal area shows about the subscription. */
export interface BusinessStatus {
  state: 'ACTIVE' | 'GRACE' | 'LAPSED' | 'NONE';
  is_vip: boolean;
  is_business: boolean;
  vip_until: string | null;
  days_left: number | null;
  ending_soon: boolean;
  grace_until: string | null;
  grace_days: number;
  paused_ads_count: number;
  free_ads_after_lapse: number;
  has_business_profile: boolean;
  should_prompt_profile: boolean;
}

/** The block every listing carries (SCRUM-262). */
export interface AdvertiserInfo {
  type: 'business' | 'private';
  label: string | null;
  /** SCRUM-287 — only present when the business switched it on. */
  contact?: AdvertiserContact;
}

/**
 * SCRUM-287 — the "פרטי העסק" block. The server sends ONLY the details the
 * business chose to show, so every field is optional.
 */
export interface AdvertiserContact {
  business_name?: string;
  logo?: string | null;
  license_number?: string;
  license_verified?: boolean;
  verified_text?: string;
  person_name?: string;
  person_avatar?: string | null;
  phone?: string;
  website?: string;
}

/** SCRUM-266 */
export interface PromotionOption {
  route_id: number;
  level: 'LEVEL2' | 'LEVEL3';
  title: string;
  label: string;
  price: number;
  days: number;
}

export interface PromotionInfo {
  id: number;
  product_id: number;
  level: string;
  label: string;
  days: number;
  amount: number;
  status: string;
  start_at: string | null;
  end_at: string | null;
}

export interface PromotionOptions {
  eligible: boolean;
  reason: string;
  active: PromotionInfo | null;
  options: PromotionOption[];
}

export interface PromotionSession {
  promotion_id: number;
  session_id: string;
  session_url: string;
  title: string;
  amount: number;
  days: number;
}

/** SCRUM-267 */
export type StatKind = 'impression' | 'open' | 'page_visit' | 'website_click' | 'phone_tap' | 'inquiry';
export type ClientEventKind = 'website_click' | 'phone_tap' | 'inquiry';

export interface StatsReport {
  from: string;
  to: string;
  labels: Record<StatKind, string>;
  totals: Record<StatKind, { organic: number; paid: number; total: number }>;
  series: Array<{ day: string } & Record<StatKind, number>>;
  ads: Array<{ id: number; name: string } & Record<StatKind, number>>;
  notes: Record<string, string>;
}

const DEFAULT_TAG_LABELS: Record<string, string> = {
  NADLAN: 'מתווך',
  CAR: 'סוחר רכב',
  default: 'עסק',
};

@Injectable({ providedIn: 'root' })
export class BusinessService {
  private config$?: Observable<BusinessConfig | null>;

  constructor(private http: HttpClient, private apiUrl: ApiUrl) {}

  /** Cached for the session — labels and domains change rarely. */
  config(): Observable<BusinessConfig | null> {
    if (!this.config$) {
      this.config$ = this.http.get<BusinessConfig>(this.apiUrl.getBusinessConfigUrl()).pipe(
        catchError(() => of(null)),
        shareReplay(1),
      );
    }
    return this.config$;
  }

  /** Never errors: a failed lookup reads as "not a business". */
  status(): Observable<BusinessStatus | null> {
    const token = this.apiUrl.getJWTToken();
    if (!token) return of(null);
    return this.http
      .get<BusinessStatus>(this.apiUrl.getBusinessStatusUrl(), {
        headers: new HttpHeaders({ Authorization: token }),
      })
      .pipe(catchError(() => of(null)));
  }

  private get authHeaders(): HttpHeaders {
    return new HttpHeaders({
      'Content-Type': 'application/json',
      Authorization: this.apiUrl.getJWTToken(),
    });
  }

  // ── SCRUM-266 ──
  promotionOptions(adId: number): Observable<PromotionOptions> {
    return this.http.get<PromotionOptions>(this.apiUrl.getPromotionOptionsUrl(adId), { headers: this.authHeaders });
  }

  initiatePromotion(adId: number, routeId: number): Observable<PromotionSession> {
    return this.http.post<PromotionSession>(
      this.apiUrl.getPromotionInitiateUrl(), { ad_id: adId, route_id: routeId }, { headers: this.authHeaders });
  }

  // ── SCRUM-267 ──
  /** Fire-and-forget. Phone tap ≠ call, website click ≠ inquiry (server keeps them apart). */
  track(kind: ClientEventKind, target: { ad_id?: number | null; seller_id?: number | null }): void {
    if (!target.ad_id && !target.seller_id) return;
    this.http
      .post(this.apiUrl.getBusinessEventsUrl(), { events: [{ kind, ...target }] }, {
        headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
      })
      .pipe(catchError(() => of(null)))
      .subscribe();
  }

  stats(opts: { from?: string; to?: string; ad?: number | null }): Observable<StatsReport> {
    let params = new HttpParams();
    if (opts.from) params = params.set('from', opts.from);
    if (opts.to) params = params.set('to', opts.to);
    if (opts.ad) params = params.set('ad', String(opts.ad));
    return this.http.get<StatsReport>(this.apiUrl.getBusinessStatsUrl(), { headers: this.authHeaders, params });
  }

  /**
   * The business tag of a listing, or null for a private advertiser.
   * Prefers the server's `advertiser` block; falls back to the seller's
   * `advertiser_type` + the listing's resourcetype for payloads that predate it.
   */
  /** SCRUM-287 — the ad's business contact block, or null. */
  static contactFor(product: any): AdvertiserContact | null {
    const adv = product?.advertiser;
    if (!adv || adv.type !== 'business' || !adv.contact) return null;
    return Object.keys(adv.contact).length ? adv.contact : null;
  }

  static tagFor(product: any): string | null {
    if (!product) return null;
    // Internal (site) ads never carry an advertiser tag.
    if (String(product.resourcetype || '').toLowerCase() === 'internalads') return null;
    const adv: AdvertiserInfo | undefined = product.advertiser;
    if (adv) return adv.type === 'business' ? adv.label || DEFAULT_TAG_LABELS['default'] : null;
    const user = product.User || product.user;
    if (user?.advertiser_type !== 'business') return null;
    const key = String(product.resourcetype || '').toUpperCase();
    return DEFAULT_TAG_LABELS[key] || DEFAULT_TAG_LABELS['default'];
  }
}
